import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import '@chancenwerk/components/chancen-component-choose-digital-courses.js';

import { ChancenDialogTimeMatrix } from '@chancenwerk/components/dialogs/chancen-dialog-time-matrix.js';
import '@chancenwerk/components/notifications/chancen-component-notification-list.js';
import { Course } from '@chancenwerk/db/courses/Course.js';
import { CourseDigital, Subject } from '@chancenwerk/db/courses/CourseDigital.js';
import { CourseFilter } from '@chancenwerk/db/filter/CourseFilter.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { StudentParticipationRelationship } from '@chancenwerk/db/semantics/StudentParticipationRelationship.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { combineLatest, firstValueFrom, switchMap } from 'rxjs';
import '../components/campus-component-section-lesson-list.js';
import { CampusRoutes } from '../routes.js';
import './campus-page-no-permission.js';

const courseSelectorDigital: BaseQLSelectionSet<CourseDigital> = {
  _class: 1,
  _id: 1,
  weekday: 1,
  startTime: 1,
  endTime: 1,
  linkedKPFIDs: 1,
  linkedKPFs: 1,
  location: 1,
  osStudentIDs: 1,
  jahrgangsstufe: 1,
  subject: 1,
};

/**
 * @customElement campus-student-choose-digital-courses
 */
@customElement('campus-student-choose-digital-courses')
export class CampusStudentChooseIntensiveCourses extends ChemistryLitElement {
  private readonly size = new WindowSizeController(this);

  override styles = () => [
    {
      ':host': {
        // height: '100%',
        width: '100%',
      },
    },
  ];

  @state() private readonly _currentUser = new RXController(this, currentUser<Student>());
  @state() private readonly _subjects = new RXController(this, ['', '', '']);

  @state() private readonly _kpfId = new RXController(
    this,
    this._currentUser.observable.pipe(
      switchMap(async student => {
        if (!student) {
          return null;
        }

        const rel = await firstValueFrom(
          StudentParticipationRelationship.subscribeStudentParticipationRelationshipsForUser(student._id)({
            objectID: 1,
          }),
        );
        return rel[0]?.objectID ?? null;
      }),
    ),
  );

  @state() private readonly _jahrgang = new RXController(
    this,
    this._currentUser.observable.pipe(
      switchMap(async student => {
        if (!student) {
          return 0;
        }

        return student.schoolInformation.jahrgangsstufe;
      }),
    ),
  );

  @state() private readonly _digitalCourses = new RXController(
    this,
    combineLatest([this._jahrgang.observable, this._subjects.observable]).pipe(
      switchMap(values => {
        const jahrgangsstufe = values[0];
        return CourseDigital.subscribeDigitialCourseSubjectAndGrade(
          values[1].filter(e => e !== ''),
          jahrgangsstufe ?? 0,
        )(courseSelectorDigital);
      }),
    ),
  );

  @state() private _digitalCoursesChoosen: CourseDigital[] = [];

  override connectedCallback(): void {
    super.connectedCallback();

    this._currentUser.observable.subscribe(async u => {
      if (!u) {
        return;
      }

      this._digitalCoursesChoosen = (await Course.getCourseForUser(
        u._id,
        new CourseFilter({ courseType: CourseDigital._class }),
      )(courseSelectorDigital)) as CourseDigital[];

      if (this._digitalCoursesChoosen.length > 0) {
        goTo(CampusRoutes.dashboard);
      }

      this._subjects.next([u.preferedFirstSubject ?? '', u.preferedSecondSubject ?? '', u.preferedThirdSubject ?? '']);
    });
  }

  private async _save() {
    try {
      if (!this._kpfId.value) {
        throw new Error('KPF konnte nicht festgestellt werden!');
      }
      const currentUser = this._currentUser.value;
      if (!currentUser) throw new Error('No User found');

      if (
        !(await Student.setOwnPreferedSubjects(
          this._subjects.value[0] ?? '',
          this._subjects.value[1] ?? '',
          this._subjects.value[2] ?? '',
        )())
      ) {
        throw new Error('Fehler beim Speichern der Kurswahl');
      }

      for (const choosenDigitalCourse of this._digitalCoursesChoosen) {
        if (!choosenDigitalCourse.linkedKPFIDs) choosenDigitalCourse.linkedKPFIDs = [];
        if (!choosenDigitalCourse.linkedKPFIDs.includes(this._kpfId.value)) {
          choosenDigitalCourse.linkedKPFIDs.push(this._kpfId.value);
        }
        if (!choosenDigitalCourse.osStudentIDs.includes(currentUser._id)) {
          choosenDigitalCourse.osStudentIDs.push(currentUser._id);
        }
        await choosenDigitalCourse.save();
      }

      await Student.sendStudentUpdateDigitalIKNotification(currentUser, this._kpfId.value)();

      void ChromiumSnackbar.show('Kurswahl gespeichert');
      goTo(CampusRoutes.dashboard);
    } catch (err: any) {
      if (err instanceof MultipleValidationError)
        return ChromiumSnackbar.show(err.extensions.validationErrors?.[0]?.message ?? err.message);
      return ChromiumSnackbar.show(err.message);
    }
  }

  override render() {
    return permissionGuard(ChancenPermission.campus.READ_CONTENT, () => this._content(), {
      forbiddenTemplate: html`<campus-page-no-permission></campus-page-no-permission>`,
      context: ANY_CONTEXT,
    });
  }

  private _content() {
    return html`<cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
      <cr-flex space="lg">
        <cr-h2> Digitale Intensivkurse </cr-h2>
        <cr-text>
          <cr-text bold>Hier kannst du deinen digitalen Intensivkurs auswählen!</cr-text>

          <br />

          Aktuell werden diese digitalen Intensivkurse zu deinem Wunschfach zu folgenden Uhrzeiten bereits angeboten.
          Wähle hier gerne ein bis zwei digitale Intensivkurse aus, an denen du teilnehmen möchtest
        </cr-text>

        <cr-flex space="sm" ${css({ maxWidth: '480px' })}>
          ${['Erstwunsch', 'Zweitwunsch', 'Drittwunsch'].map((title, i) => {
            return html`<cr-dropdown-search-selection
              clearable
              placeholder=${title}
              .value=${this._subjects.value[i]}
              .selectable=${Object.values(Subject).filter(s => s !== Subject.NATURWISSENSCHAFTEN)}
              .search=${(search?: string) =>
                Object.values(Subject)
                  .filter(s => s !== Subject.NATURWISSENSCHAFTEN)
                  .filter(x => x.toLowerCase().includes(search?.toLowerCase() ?? ''))}
              @value-picked=${e => {
                this._subjects.value[i] = e.detail.value;
                this._subjects.next(this._subjects.value);
              }}
            ></cr-dropdown-search-selection>`;
          })}
          <cr-button
            mode="outline"
            @click=${async () => {
              if (!this._currentUser.value) throw new Error('No User found');

              const student = await ChancenDialogTimeMatrix.showPopup<Student>({
                props: { student: this._currentUser.value },
              });

              this._currentUser.value.daysAvailable = student.daysAvailable;
              this.requestUpdate();
            }}
          >
            Keinen digitalen Intensivkurs gefunden?
          </cr-button>
        </cr-flex>

        ${this._subjects.value.filter(e => e !== '').length > 0
          ? html`<chancen-component-choose-digital-courses
              .student=${this._currentUser.value}
              .digitalCourses=${this._digitalCourses.value}
              .digitalCoursesChoosen=${this._digitalCoursesChoosen}
              horizontal
              @value-picked=${e => {
                this._digitalCoursesChoosen = e.detail.value;
                this.requestUpdate();
              }}
            ></chancen-component-choose-digital-courses>`
          : nothing}
        <cr-flex horizontal space="sm">
          <cr-button
            mode="outline"
            @click=${async () => {
              const student = await ChancenDialogTimeMatrix.showPopup<Student>({
                props: { student: this._currentUser.value },
              });
              this._currentUser.value!.daysAvailable = student.daysAvailable;
              this.requestUpdate();
            }}
          >
            Keinen digitalen Intensivkurs gefunden?
          </cr-button>
          <cr-button ${css({ maxWidth: '150px' })} @click=${this._save}>SPEICHERN</cr-button>
        </cr-flex>
      </cr-flex>
    </cr-flex>`;
  }
}
